import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/authContext';
import { 
  format, 
  startOfMonth, 
  endOfMonth, 
  eachDayOfInterval, 
  isToday, 
  isWeekend, 
  parseISO,
  getDay,
  addDays,
  isSameMonth
} from 'date-fns';
import es from 'date-fns/locale/es';
import { getAllEvents, createEvent, deleteEvent, updateEvent } from '../services/eventsServices';
import Swal from 'sweetalert2';
import { useLocation } from 'react-router-dom';
import { Banner } from "../components/common/Banner";
import EventModal from '../components/events/EventModal';
import EventFormModal from '../components/events/EventFormModal';
import SectionTips from '../components/common/SectionTips';

const CalendarMain = () => {
  const { user } = useAuth();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showEventModal, setShowEventModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const location = useLocation();
  const eventFromState = location.state?.event;
  const [eventData, setEventData] = useState({
    business_name: user.business_name,
    organized_by: user.business_name,
    collaborator: '',
    title: '',
    description: '',
    date: format(new Date(), 'yyyy-MM-dd'),
    hour: '12:00',
    hour_end: '13:00',
    place: '',
    registration_link: '',
    meet_link: '',
    mode: 'in-person',
    image: '',
    id_events: null
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setIsFetching(true);
        const [eventsData, holidaysData] = await Promise.all([
          getAllEvents(user),
          axios.get(`https://date.nager.at/api/v3/PublicHolidays/${currentDate.getFullYear()}/CL`)
        ]);
        
        setEvents(eventsData);
        
        const filteredHolidays = holidaysData.data
          .filter(holiday => holiday.global)
          .map(holiday => ({
            date: parseISO(holiday.date),
            name: holiday.localName,
          }));
        
        setHolidays(filteredHolidays);
      } catch (error) {
        console.error('Error fetching data:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error al cargar datos',
          text: 'No se pudieron cargar los eventos o feriados',
          timer: 2000,
        });
      } finally {
        setLoading(false);
        setIsFetching(false);
      }
    };

    fetchData();
  }, [currentDate.getFullYear()]);

  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const startDay = getDay(monthStart);
  
  const daysToShow = eachDayOfInterval({
    start: addDays(monthStart, -((startDay + 6) % 7)),
    end: monthEnd
  });

  const changeMonth = (months) => {
    setCurrentDate(prev => new Date(prev.getFullYear(), prev.getMonth() + months, 1));
  };

  const handleEventClick = (event) => {
    setSelectedEvent(event);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedEvent(null);
  };

  const handleDayClick = async (date, clickedOnEvent = false) => {
    const dayData = getDayData(date);
    
    // Si se hizo click directamente en un evento, mostrar ese evento
    if (clickedOnEvent) {
      return;
    }
    
    if (dayData.events.length > 0) {
      const result  = await Swal.fire({
        title: '¿Deseas crear un evento para este día?',
        text: format(date, 'PPPP', { locale: es }),
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#0488AF',
        cancelButtonColor: '#6c757d',
        confirmButtonText: 'Sí, crear evento',
        cancelButtonText: 'Cancelar'
      });
      
      if (result.isConfirmed) {
        openCreateModal(date);
      }
    } else {
      // Si no hay eventos, preguntar si quiere crear uno
      const result = await Swal.fire({
        title: '¿Deseas crear un evento para este día?',
        text: format(date, 'PPPP', { locale: es }),
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#0488AF',
        cancelButtonColor: '#6c757d',
        confirmButtonText: 'Sí, crear evento',
        cancelButtonText: 'Cancelar'
      });
      
      if (result.isConfirmed) {
        openCreateModal(date);
      }
    }
  };

  const openCreateModal = (date = new Date()) => {
    setEventData({
      business_name: user.business_name,
      organized_by: user.business_name,
      collaborator: '',
      title: '',
      description: '',
      date: format(date, 'yyyy-MM-dd'),
      hour: '12:00',
      hour_end: '13:00',
      place: '',
      registration_link: '',
      meet_link: '',
      mode: 'in-person',
      image: '',
    });
    setIsEditing(false);
    setShowEventModal(true);
  };

  useEffect(() => {
    if (eventFromState) {
      handleEventClick(eventFromState);
    }
  }, [eventFromState]);

  const openEditModal = (event) => {
    setEventData({
      ...event,
      id_events: event.id_events
    });
    setIsEditing(true);
    setShowEventModal(true);
  };

  const closeEventModal = () => {
    setShowEventModal(false);
    setEventData({
      business_name: user.business_name,
      organized_by: user.business_name,
      collaborator: '',
      title: '',
      description: '',
      date: format(new Date(), 'yyyy-MM-dd'),
      hour: '12:00',
      hour_end: '13:00',
      place: '',
      registration_link: '',
      meet_link: '',
      mode: 'in-person',
      image: ''
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEventData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setEventData(prev => ({
          ...prev,
          image: reader.result
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      if (isEditing) {
        await updateEvent(eventData.id_events, eventData, user);
        Swal.fire({
          icon: 'success',
          title: 'Evento actualizado',
          showConfirmButton: false,
          timer: 1500,
        });
      } else {
        await createEvent(eventData, user);
        Swal.fire({
          icon: 'success',
          title: 'Evento creado',
          showConfirmButton: false,
          timer: 1500,
        });
      }
      
      const eventsData = await getAllEvents(user);
      setEvents(eventsData);
      closeEventModal();
    } catch (error) {
      console.error('Error saving event:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'No se pudo guardar el evento. Por favor intente nuevamente.',
        timer: 2000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteEvent = async () => {
    const confirmation = await Swal.fire({
      title: '¿Estás seguro de eliminar el evento?',
      text: 'Esta acción no se puede deshacer',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar',
    });
  
    if (!confirmation.isConfirmed) return;
  
    try {
      setIsDeleting(true);
      await deleteEvent(selectedEvent.id_events, user);
      
      Swal.fire({
        icon: 'success',
        title: 'Evento eliminado',
        showConfirmButton: false,
        timer: 1500,
      });
      
      const eventsData = await getAllEvents(user);
      setEvents(eventsData);
      closeModal();
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error al eliminar el evento',
        text: error.message || 'Inténtalo de nuevo más tarde',
        timer: 1500,
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const parseDateToLocal = (dateString) => {
    if (!dateString) return null;
  
    const cleanDateString = dateString.split('T')[0];
    const parts = cleanDateString.split('-');
  
    if (parts.length !== 3) return null;
  
    const [year, month, day] = parts.map(Number);
  
    if (isNaN(year) || isNaN(month) || isNaN(day)) return null;
  
    return new Date(year, month - 1, day);
  };
  
  const getDayData = (date) => ({
    isCurrent: isToday(date),
    isHoliday: holidays.some(h => format(h.date, 'yyyy-MM-dd') === format(date, 'yyyy-MM-dd')),
    isWeekend: isWeekend(date),
    isCurrentMonth: isSameMonth(date, currentDate),
    holiday: holidays.find(h => format(h.date, 'yyyy-MM-dd') === format(date, 'yyyy-MM-dd')),
    events: events.filter(e => 
      format(parseDateToLocal(e.date), 'yyyy-MM-dd') === format(date, 'yyyy-MM-dd')
    )
  });
  
  const dayStyles = (dayData) => {
    if (!dayData.isCurrentMonth) return 'bg-gray-100 text-gray-400';
    if (dayData.isHoliday) return 'bg-red-50 text-red-600 border-red-200';
    if (dayData.isCurrent) return 'bg-blue-50 text-blue-600 border-blue-300';
    if (dayData.isWeekend) return 'bg-gray-50 text-gray-600';
    return 'bg-white text-gray-800';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        <span className="ml-3 text-gray-700">Cargando eventos...</span>
      </div>
    );
  }

  return (
    <div className="p-4 max-w-screen-xl mx-auto px-4 py-8">
      <Banner title="Eventos disponibles" message="Con cada evento, se construye una nueva oportunidad"/>
      <SectionTips
        section="la sección de eventos"
        buttonTitle="¿Cómo usar esta sección?"
        tipsTitle="¿Cómo crear y visualizar los eventos?"
        tipsList={[
          'Puedes crear tus eventos mendiante el boton "Crear Evento".',
          'Revisa el detalle de cada evento del mes.',
          'Haz click en cualquier día para crear un evento en esa fecha.'
        ]}
        finalNote="Consejo: Planifica con anticipación tu evento."
        color="#1976d2"
      />

      {/* Calendar Controls */}
      <div className="flex justify-between items-center mb-6">
        <button
          onClick={() => changeMonth(-1)}
          disabled={isFetching}
          className="flex items-center justify-center px-2 py-1 rounded-lg text-white transition-colors disabled:opacity-50"
          style={{
            background: '#0488AF',
            color: '#fff',
            textTransform: 'none',
            minWidth: 40,
            minHeight: '32px',
            padding: '4px 8px',
            lineHeight: '1.5',
            border: 'none',
          }}
          onMouseOver={(e) => {
            e.target.style.background = '#03779C';
          }}
          onMouseOut={(e) => {
            e.target.style.background = '#0488AF';
          }}
        >
          <span className="flex items-center justify-center">
            {isFetching ? (
              <svg
                className="animate-spin -ml-1 mr-1 h-4 w-4 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
            ) : (
              <span className="text-base">&larr;</span>
            )}
          </span>
        </button>

        <h2 className="text-2xl font-bold text-gray-800 absolute left-1/2 transform -translate-x-1/2">
          {format(currentDate, 'MMMM yyyy', { locale: es }).toUpperCase()}
        </h2>

        <div className="flex items-center space-x-4 mr-6">
          <button
            onClick={() => openCreateModal(new Date())}
            disabled={isFetching}
            className="flex items-center px-4 py-2 rounded-lg text-white transition-colors disabled:opacity-50"
            style={{
              background: 'linear-gradient(to right, #09274A, #0488AF)',
              color: '#fff',
              textTransform: 'none',
              minWidth: 140,
              minHeight: '36px',
              padding: '6px 16px',
              lineHeight: '1.5',
              border: 'none',
            }}
            onMouseOver={(e) => {
              e.target.style.background = 'linear-gradient(to right, #071f3d, #03779C)';
            }}
            onMouseOut={(e) => {
              e.target.style.background = 'linear-gradient(to right, #09274A, #0488AF)';
            }}
          >
            {isFetching ? (
              <svg
                className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
            ) : null}
            Crear Evento
          </button>
          <button
            onClick={() => changeMonth(+1)}
            disabled={isFetching}
            className="flex items-center justify-center px-2 py-1 rounded-lg text-white transition-colors disabled:opacity-50"
            style={{
              background: '#0488AF',
              color: '#fff',
              textTransform: 'none',
              minWidth: 40,
              minHeight: '32px',
              padding: '4px 8px',
              lineHeight: '1.5',
              border: 'none',
            }}
            onMouseOver={(e) => {
              e.target.style.background = '#03779C';
            }}
            onMouseOut={(e) => {
              e.target.style.background = '#0488AF';
            }}
          >
            {isFetching ? (
              <svg
                className="animate-spin -ml-1 mr-1 h-4 w-4 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
            ) : null}
            &rarr;
          </button>
        </div>
      </div>

      {/* Day Headers */}
      <div className="grid grid-cols-7 gap-1 mb-2">
        {['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'].map(day => (
          <div key={day} className="text-center font-semibold py-2 text-gray-600">
            {day}
          </div>
        ))}
      </div>

      {/* Calendar Days */}
      <div className="grid grid-cols-7 gap-1">
        {daysToShow.map(date => {
          const dayData = getDayData(date);
          
          return (
            <div
              key={date.toString()}
              className={`min-h-32 p-2 rounded-lg border ${dayStyles(dayData)}`}
              onClick={() => handleDayClick(date)}
            >
              <div className={`text-right font-bold ${dayData.isCurrent ? 'text-blue-700' : ''}`}>
                {format(date, 'd')}
              </div>
              
              <div className="mt-1 space-y-1 max-h-24 overflow-y-auto">
                {dayData.holiday && (
                  <div className="text-xs p-1 bg-red-100 rounded text-red-700 truncate">
                    {dayData.holiday.name}
                  </div>
                )}
                
                {dayData.events.map(event => (
                  <div 
                    key={event.id_events} 
                    className="text-xs p-1 bg-blue-100 rounded text-blue-700 truncate hover:bg-blue-200"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEventClick(event);
                    }}
                  >
                    {event.title}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Modals */}
      <EventModal
        showModal={showModal}
        selectedEvent={selectedEvent}
        closeModal={closeModal}
        openEditModal={openEditModal}
        handleDeleteEvent={handleDeleteEvent}
        isSubmitting={isSubmitting}
        isEditing={isEditing}
        isDeleting={isDeleting}
        user={user}
      />

      <EventFormModal
        showEventModal={showEventModal}
        closeEventModal={closeEventModal}
        eventData={eventData}
        setEventData={setEventData}
        handleInputChange={handleInputChange}
        handleSubmit={handleSubmit}
        isEditing={isEditing}
        isSubmitting={isSubmitting}
        user={user}
      />
    </div>
  );
};

export default CalendarMain;